import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { TableCell, TableRow } from '@mui/material';
import { makeIssueReportDetailsUrl, paths } from '../../routes';
import { useCanViewAllTenants } from '../../common/useCanViewAllTenants';
import { useAccessibleTenants } from '../../common/useAccessibleTenants';
import { getTenantDisplayName } from '../../common/tenantDisplayNames';
import { MasterProblemReport } from '../../api/master/masterApiTypes';
import { useDispatch } from 'react-redux';
import { masterApiSlice, useGetMasterProblemReportsQuery } from '../../api/master/masterApiSlice';
import { formatDate } from '../../utils';
import MasterListScaffold, { MasterColumn } from './MasterListScaffold';
import { useMasterListState } from './useMasterListState';
import { useMasterTenantData } from './useMasterTenantData';
import { onRowClick } from '../../utils/rowClick';

const ROWS_PER_PAGE_OPTIONS = [100, 200, 300, 500];
const ROWS_PER_PAGE_STORAGE_KEY = 'master_problem_reports_rows_per_page';

const parseTime = (value: string | null): number | null => {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
};

/**
 * Color map for problem-report state pills. Keys are normalized
 * lower-case state strings. Unknown states fall back to plain text.
 */
const STATE_COLORS: Record<string, { bg: string, text: string, dot: string }> = {
    open: { bg: '#FEE2E2', text: '#991B1B', dot: '#991B1B' },
    'in progress': { bg: '#CFD8FE', text: '#3F66FB', dot: '#3F66FB' },
    'on hold': { bg: '#FEF3C7', text: '#D97706', dot: '#D97706' },
    done: { bg: '#CEEFCE', text: '#2D912D', dot: '#2D912D' },
};


/**
 * Map a normalized state string to its localized display label. Uses
 * the existing `issue_reports.card.*` keys for visual consistency with
 * the regular issue-reports page. Unknown states fall back to the raw
 * value so we never crash on unexpected data.
 */
const localizeState = (
    state: string,
    t: (key: string) => string,
): string => {
    const normalized = state.toLowerCase();
    switch (normalized) {
        case 'open':
            return t('issue_reports.card.open');
        case 'in progress':
            return t('issue_reports.card.in_progress');
        case 'on hold':
            return t('issue_reports.card.on_hold');
        case 'done':
            return t('issue_reports.card.resolved');
        default:
            return state;
    }
};

const MasterProblemReportsPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language === 'de' ? 'de' : 'en';
    const canViewAllTenants = useCanViewAllTenants();
    const accessibleTenants = useAccessibleTenants();
    const list = useMasterListState({
        accessibleTenants,
        rowsPerPageStorageKey: ROWS_PER_PAGE_STORAGE_KEY,
        // Default sort: most-recently-created first.
        defaultSort: { key: 'createdAt', order: 'desc' },
        rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
    });

    const { rowsByTenant, failedTenants, loadedTenants, querySlots } =
        useMasterTenantData(accessibleTenants, useGetMasterProblemReportsQuery);

    // Reload = invalidate every tenant's cached report list; the mounted
    // query slots refetch and the rows update in place.
    const dispatch = useDispatch();
    const handleReload = React.useCallback(() => {
        dispatch(masterApiSlice.util.invalidateTags(['masterProblemReports']));
    }, [dispatch]);

    const mergedRows = useMemo(() => {
        const all: MasterProblemReport[] = [];
        accessibleTenants.forEach((tenant) => {
            if (!list.selectedTenantSlugs.has(tenant)) return;
            const rows = rowsByTenant[tenant];
            if (rows) all.push(...rows);
        });
        return all;
    }, [rowsByTenant, accessibleTenants, list.selectedTenantSlugs]);

    const filteredRows = useMemo(() => {
        const term = list.searchQuery.trim().toLowerCase();
        if (!term) return mergedRows;
        return mergedRows.filter((row) => {
            const tenantName = getTenantDisplayName(row.tenantSlug).toLowerCase();
            const bookingNumber = row.bookingNumber.toLowerCase();
            const issueType = row.issueType.toLowerCase();
            const stateRaw = row.state.toLowerCase();
            const stateLocalized = localizeState(row.state, t).toLowerCase();
            const createdBy = row.userCreatorEmail.toLowerCase();
            return (
                tenantName.includes(term) ||
                bookingNumber.includes(term) ||
                issueType.includes(term) ||
                stateRaw.includes(term) ||
                stateLocalized.includes(term) ||
                createdBy.includes(term)
            );
        });
    }, [mergedRows, list.searchQuery, t]);

    const sortedRows = useMemo(() => {
        const compare = (a: MasterProblemReport, b: MasterProblemReport): number => {
            switch (list.sortState.key) {
                case 'tenant':
                    return getTenantDisplayName(a.tenantSlug)
                        .localeCompare(getTenantDisplayName(b.tenantSlug));
                case 'booking':
                    return a.bookingNumber.localeCompare(b.bookingNumber);
                case 'type':
                    return a.issueType.localeCompare(b.issueType);
                case 'state':
                    return a.state.toLowerCase().localeCompare(b.state.toLowerCase());
                case 'createdBy':
                    return a.userCreatorEmail.localeCompare(b.userCreatorEmail);
                case 'createdAt': {
                    const aRef = parseTime(a.createdAt) ?? 0;
                    const bRef = parseTime(b.createdAt) ?? 0;
                    return aRef - bRef;
                }
                default:
                    return 0;
            }
        };
        return list.applySort(filteredRows, compare);
    }, [filteredRows, list.applySort, list.sortState.key]);

    const pagedRows = useMemo(
        () => list.paginate(sortedRows),
        [sortedRows, list.paginate],
    );

    const handleRowClick = React.useCallback((row: MasterProblemReport) => {
        list.drillIntoTenant(row.tenantSlug, makeIssueReportDetailsUrl(row.id));
    }, [list.drillIntoTenant]);

    if (!canViewAllTenants) {
        return <Navigate to={paths.unauthorized} replace />;
    }

    const total = accessibleTenants.length;
    const loaded = loadedTenants.size;
    const failedCount = failedTenants.size;
    const notApplicable = t('common.not_applicable');
    const allLoaded = total > 0 && loaded === total;
    const showEmptyState =
        allLoaded && failedCount === 0 && sortedRows.length === 0;


    const renderStatePill = (state: string) => {
        if (!state) return <span>{notApplicable}</span>;
        const normalized = state.toLowerCase();
        const colors = STATE_COLORS[normalized];
        const label = localizeState(state, t);
        if (!colors) {
            return <span>{label}</span>;
        }
        return (
            <span
                className={
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium'
                }
                style={{ backgroundColor: colors.bg, color: colors.text }}
            >
                <span
                    className={'inline-block rounded-full'}
                    style={{
                        width: 6,
                        height: 6,
                        backgroundColor: colors.dot,
                    }}
                />
                {label}
            </span>
        );
    };

    const columns: MasterColumn[] = [
        { label: t('master.columns.tenant'), sortKey: 'tenant' },
        { label: t('master.columns.booking_number'), sortKey: 'booking' },
        { label: t('master.columns.issue_type'), sortKey: 'type' },
        { label: t('master.columns.state'), sortKey: 'state' },
        { label: t('master.columns.created_by'), sortKey: 'createdBy' },
        { label: t('master.columns.created_at'), sortKey: 'createdAt' },
    ];

    return (
        <MasterListScaffold
            controls={list}
            title={t('master.problem_reports')}
            subtitle={t('master.problem_reports_count', { count: sortedRows.length })}
            searchPlaceholder={t('master.problem_reports_search_placeholder')}
            columns={columns}
            totalCount={sortedRows.length}
            loadedTenants={loaded}
            totalTenants={total}
            failedTenants={Array.from(failedTenants)}
            showEmptyState={showEmptyState}
            emptyMessage={t('master.problem_reports_empty')}
            querySlots={querySlots}
            onReload={handleReload}
        >
            {pagedRows.map((row) => (
                <TableRow
                    key={`${row.tenantSlug}-${row.id}`}
                    hover={true}
                    sx={{ cursor: 'pointer' }}
                    onClick={onRowClick(() => handleRowClick(row))}
                >
                    <TableCell>
                        {getTenantDisplayName(row.tenantSlug)}
                    </TableCell>
                    <TableCell>
                        {row.bookingNumber || notApplicable}
                    </TableCell>
                    <TableCell>
                        {row.issueType || notApplicable}
                    </TableCell>
                    <TableCell>{renderStatePill(row.state)}</TableCell>
                    <TableCell>
                        {row.userCreatorEmail || notApplicable}
                    </TableCell>
                    <TableCell>
                        {row.createdAt
                            ? formatDate(row.createdAt, language)
                            : notApplicable}
                    </TableCell>
                </TableRow>
            ))}
        </MasterListScaffold>
    );
};

export default MasterProblemReportsPage;
